// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: tri de la liste des actions — comparateur pur, résolveurs injectés
// ═══════════════════════════════════════════════════════════════════════════
//
// Extrait de page-actions/render-page.ts (juin 2026). La logique de tri (12
// critères, sens asc/desc, sentinelles de fin de tri 'zzzz'/99999) est pure ;
// les dépendances qui touchent au DOM ou aux maps de la page (résolution des
// trigrammes RACI, nom de statut, code projet, date du jour) sont INJECTÉES
// via `ctx` pour rester testables sans la closure de render-page.
//
// Les helpers de dates (effectiveDeadline / remainingDays) sont réutilisés
// depuis domain/eisenhower — source unique, plus de copie locale dans la page.

import { effectiveDeadline, remainingDays, type ClassifiableAction } from './eisenhower.js';

/** Forme minimale d'une action pour le tri. */
export interface SortableAction extends ClassifiableAction {
  id?: number | null;
  text?: string;
  performers?: unknown;
  consulted?: unknown;
  priority?: number | null;
  deadline?: string | null;
  deadline_revised?: string | null;
  approver_name?: string | null;
  status_id?: number | null;
  updated_at?: string | null;
  project_ids?: number[];
  origins?: Array<{ project_code?: string | null; risk_code?: string | null }>;
}

/** Résolveurs injectés (dépendances DOM / maps de la page). */
export interface SortContext {
  /** Liste d'ids RACI → chaîne triable (ex. trigrammes joints, casse libre). */
  nameOf: (ids: unknown) => string;
  /** Nom du statut pour le tri (chaîne vide si inconnu). */
  statusName: (statusId: number | null | undefined) => string;
  /** Code projet pour le tri (chaîne vide si inconnu). */
  projectCode: (projectId: number) => string;
  /** Date du jour 'YYYY-MM-DD' (pour le tri « jours restants »). */
  today: string;
}

/**
 * Trie une copie de `actions` selon `sortKey` / `sortDir`. Critères inconnus →
 * tri par id. Stable vis-à-vis de l'entrée (ne mute pas le tableau source).
 */
export function sortActions<T extends SortableAction>(
  actions: T[],
  sortKey: string,
  sortDir: string,
  ctx: SortContext,
): T[] {
  return [...actions].sort((a, b) => {
    let va: string | number;
    let vb: string | number;
    switch (sortKey) {
      case 'code':
        va = a.id ?? 99999;
        vb = b.id ?? 99999;
        break;
      case 'text':
        va = (a.text || '').toLowerCase();
        vb = (b.text || '').toLowerCase();
        break;
      case 'actors':
        va = ctx.nameOf(a.performers).toLowerCase();
        vb = ctx.nameOf(b.performers).toLowerCase();
        break;
      case 'consulted':
        va = ctx.nameOf(a.consulted).toLowerCase();
        vb = ctx.nameOf(b.consulted).toLowerCase();
        break;
      case 'priority':
        va = a.priority ?? 99;
        vb = b.priority ?? 99;
        break;
      case 'deadline':
        va = effectiveDeadline(a) || 'zzzz';
        vb = effectiveDeadline(b) || 'zzzz';
        break;
      case 'deadline_init':
        va = a.deadline || 'zzzz';
        vb = b.deadline || 'zzzz';
        break;
      case 'remaining':
        va = remainingDays(a, ctx.today) ?? 99999;
        vb = remainingDays(b, ctx.today) ?? 99999;
        break;
      case 'owner':
        va = (a.approver_name || '').toLowerCase();
        vb = (b.approver_name || '').toLowerCase();
        break;
      case 'status':
        va = ctx.statusName(a.status_id).toLowerCase();
        vb = ctx.statusName(b.status_id).toLowerCase();
        break;
      case 'updated':
        va = a.updated_at || '';
        vb = b.updated_at || '';
        break;
      case 'projects': {
        const fa =
          (a.project_ids || [])
            .map((id) => ctx.projectCode(id))
            .filter(Boolean)
            .sort()[0] || 'zzzz';
        const fb =
          (b.project_ids || [])
            .map((id) => ctx.projectCode(id))
            .filter(Boolean)
            .sort()[0] || 'zzzz';
        va = fa.toLowerCase();
        vb = fb.toLowerCase();
        break;
      }
      case 'risks': {
        const sa =
          (a.origins || [])
            .map((o) => `${o.project_code || 'zz'}-${o.risk_code || 'zz'}`)
            .sort()[0] || 'zzzz';
        const sb =
          (b.origins || [])
            .map((o) => `${o.project_code || 'zz'}-${o.risk_code || 'zz'}`)
            .sort()[0] || 'zzzz';
        va = sa.toLowerCase();
        vb = sb.toLowerCase();
        break;
      }
      default:
        va = a.id ?? 0;
        vb = b.id ?? 0;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    // Départage DÉTERMINISTE des ex æquo par id (toujours croissant, quel que
    // soit le sens) : sans lui, l'ordre des lignes à clé égale dépend de l'ordre
    // d'ENTRÉE (donc de l'ordre renvoyé par le serveur). Après l'édition d'une
    // action, la liste est re-fetchée dans un ordre potentiellement différent →
    // les ex æquo « sautaient ». Avec ce tie-break, l'ordre reste stable d'un
    // re-fetch à l'autre (bug #7).
    const ida = a.id ?? 0;
    const idb = b.id ?? 0;
    return ida - idb;
  });
}
